import { AutopodderResultRow, ScheduleDataItem } from "../types";

export interface DayStats {
    dateStr: string;            // 'MM/DD/YYYY' from row.DATE
    isoKey: string;             // 'YYYY-MM-DD' for sortable map keys
    dayOfWeek: string;          // 'Mon', 'Tue', ...
    slots: AutopodderResultRow[];      // every row, media + episode
    mediaSlots: AutopodderResultRow[]; // ad break rows only
    slotCount: number;          // count of media slots (breaks)
    totalCapacity: number;      // sum(SIZE_MAX) over media
    totalFilled: number;        // sum(SELECTED_AD_COMBO_TOTAL_ADS_LENGTH)
    totalUnfilled: number;
    fillPct: number;            // 0..100
    totalDollars: number;
    advertiserCounts: { advertiserId: string; count: number; color: string }[];
    maxPerDayViolations: { adName: string; used: number; cap: number }[];
}

export interface CalendarSummary {
    days: DayStats[];
    byKey: Record<string, DayStats>;
    totalDollars: number;
    totalUnfilled: number;
    totalSlots: number;
    fillPct: number;
    daysWithViolations: number;
}

const MONTHS_LONG = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

// Accepts the variants the autopodder pipeline emits:
//   08/22/2025  (4-digit year, zero-padded)   — syndication SQL example
//   5/4/26      (2-digit year, no padding)    — broadcast result files
//   8/1/25, 05/04/2026, etc.
// 2-digit years are taken as 2000-relative — the autopodder isn't writing
// rows for the 1900s. Returning a normal Date that getFullYear() reads back
// as the 4-digit year, never 19YY.
export function parseMmddyyyy(s: string): Date {
    const [mm, dd, yy] = s.split("/").map(Number);
    const year = yy < 100 ? 2000 + yy : yy;
    return new Date(year, mm - 1, dd);
}

export function isoKey(d: Date): string {
    const m = d.getMonth() + 1;
    const dd = d.getDate();
    return `${d.getFullYear()}-${m < 10 ? "0" + m : m}-${dd < 10 ? "0" + dd : dd}`;
}

export function formatMonthYear(d: Date): string {
    return `${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}`;
}

// Seconds-of-day for a slot, parsed from DATE_TIME — the only time field that
// is reliably populated across broadcast and syndication batch jobs (the TIME
// column is empty in some broadcast outputs). Works for both "YYYY-MM-DD HH:MM:SS"
// (snowflake-sdk string form) and "YYYY-MM-DDTHH:MM:SS.sssZ" (JSON-serialized
// Date form). Returns 0 if DATE_TIME is missing/unparseable.
export function slotSeconds(row: Pick<AutopodderResultRow, "DATE_TIME">): number {
    if (!row.DATE_TIME) return 0;
    const m = row.DATE_TIME.match(/(\d{1,2}):(\d{1,2}):(\d{1,2})/);
    if (!m) return 0;
    const hh = Number(m[1]);
    const mm = Number(m[2]);
    const ss = Number(m[3]);
    if (!Number.isFinite(hh) || !Number.isFinite(mm) || !Number.isFinite(ss)) return 0;
    return hh * 3600 + mm * 60 + ss;
}

// Hash → palette color, mirroring colorUtils so the calendar can compute
// stripes without importing react components.
import { getColorForAdvertiser } from "../utils/colorUtils";

export function aggregate(
    poddedData: AutopodderResultRow[],
    scheduleData: Record<string, ScheduleDataItem>,
): CalendarSummary {
    const byKey: Record<string, DayStats> = {};

    for (const row of poddedData) {
        const d = parseMmddyyyy(row.DATE);
        const key = isoKey(d);
        if (!byKey[key]) {
            byKey[key] = {
                dateStr: row.DATE,
                isoKey: key,
                dayOfWeek: row.DAY_OF_WEEK,
                slots: [],
                mediaSlots: [],
                slotCount: 0,
                totalCapacity: 0,
                totalFilled: 0,
                totalUnfilled: 0,
                fillPct: 0,
                totalDollars: 0,
                advertiserCounts: [],
                maxPerDayViolations: [],
            };
        }
        const day = byKey[key];
        day.slots.push(row);
        if (row.TYPE === "media") {
            day.mediaSlots.push(row);
            day.slotCount++;
            day.totalCapacity += row.SIZE_MAX;
            day.totalFilled += row.SELECTED_AD_COMBO_TOTAL_ADS_LENGTH;
            day.totalDollars += row.SELECTED_AD_COMBO_TOTAL_DOLLARS;
        }
    }

    for (const day of Object.values(byKey)) {
        day.slots.sort((a, b) => slotSeconds(a) - slotSeconds(b));
        day.mediaSlots.sort((a, b) => slotSeconds(a) - slotSeconds(b));
        day.totalUnfilled = Math.max(0, day.totalCapacity - day.totalFilled);
        day.fillPct = day.totalCapacity > 0 ? (day.totalFilled / day.totalCapacity) * 100 : 0;

        const adUsage: Record<string, number> = {};
        const advUsage: Record<string, number> = {};

        for (const slot of day.mediaSlots) {
            const ads = slot.SELECTED_AD_COMBO_ADS_STR
                ? slot.SELECTED_AD_COMBO_ADS_STR.split(",").map((s) => s.trim()).filter(Boolean)
                : [];
            for (const adName of ads) {
                adUsage[adName] = (adUsage[adName] ?? 0) + 1;
                const sched = scheduleData[adName];
                if (sched?.ADVERTISER_ID) {
                    advUsage[sched.ADVERTISER_ID] = (advUsage[sched.ADVERTISER_ID] ?? 0) + 1;
                }
            }
        }

        for (const [adName, used] of Object.entries(adUsage)) {
            const sched = scheduleData[adName];
            const cap = sched?.MAX_PER_DAY ?? 0;
            if (cap > 0 && used > cap) {
                day.maxPerDayViolations.push({ adName, used, cap });
            }
        }

        day.advertiserCounts = Object.entries(advUsage)
            .map(([advertiserId, count]) => ({
                advertiserId,
                count,
                color: getColorForAdvertiser(advertiserId),
            }))
            .sort((a, b) => b.count - a.count);
    }

    const days = Object.values(byKey).sort((a, b) => a.isoKey.localeCompare(b.isoKey));

    let totalDollars = 0;
    let totalUnfilled = 0;
    let totalCapacity = 0;
    let totalFilled = 0;
    let daysWithViolations = 0;
    let totalSlots = 0;

    for (const d of days) {
        totalDollars += d.totalDollars;
        totalUnfilled += d.totalUnfilled;
        totalCapacity += d.totalCapacity;
        totalFilled += d.totalFilled;
        totalSlots += d.slotCount;
        if (d.maxPerDayViolations.length > 0) daysWithViolations++;
    }

    return {
        days,
        byKey,
        totalDollars,
        totalUnfilled,
        totalSlots,
        fillPct: totalCapacity > 0 ? (totalFilled / totalCapacity) * 100 : 0,
        daysWithViolations,
    };
}

// Build a calendar-month grid (rows of 7 cells, Sun→Sat). The grid spans the
// full calendar month, padded into the surrounding week boundaries. `byKey`
// supplies overlay data for any cell that has it; everything else is just an
// empty calendar day.
export function buildMonthGrid(
    monthStart: Date,
    monthEnd: Date,
    byKey: Record<string, DayStats>,
): Array<Array<{ date: Date; isoKey: string; stats: DayStats | null }>> {
    const start = new Date(monthStart);
    start.setDate(start.getDate() - start.getDay()); // back to Sunday

    const end = new Date(monthEnd);
    end.setDate(end.getDate() + (6 - end.getDay())); // forward to Saturday

    const weeks: Array<Array<{ date: Date; isoKey: string; stats: DayStats | null }>> = [];
    const cur = new Date(start);
    while (cur <= end) {
        const week: Array<{ date: Date; isoKey: string; stats: DayStats | null }> = [];
        for (let i = 0; i < 7; i++) {
            const key = isoKey(cur);
            week.push({
                date: new Date(cur),
                isoKey: key,
                stats: byKey[key] ?? null,
            });
            cur.setDate(cur.getDate() + 1);
        }
        weeks.push(week);
    }
    return weeks;
}
